// US phone-number formatting and normalization for the portal's phone inputs.
// Pure so it can be unit-tested and reused by PhoneField / PhoneListField.
//
// Two distinct jobs, deliberately separate:
//   • formatUsPhone  — DISPLAY. Masks progressively while a broker types.
//     Forgiving by design: it never rejects, it just shows what it can.
//   • normalizeUsPhone — the CLIENT-SIDE GATE (BRK-S073). It answers the same
//     accept/reject question the PMS write path answers, so a broker learns a
//     number is unusable inline instead of after a round-trip. PMS stays
//     authoritative — this only moves its verdict earlier.
//
// US-only is a deliberate scope decision (PMS-S173), not an oversight.
//
// ─── INTENTIONAL MIRROR ──────────────────────────────────────────────────────
// `formatUsPhone`, `formatE164ForDisplay` and `normalizeUsPhone` are mirrored
// VERBATIM from the PMS helper (pms-local `lib/phone.ts`), and PHONE_LINE_TYPES
// mirrors its `phoneLineType` pgEnum. The two files must stay readable side by
// side: change one, change the other, or the portal starts accepting numbers the
// API rejects (or vice versa). The parity is pinned by test/lib/phone.test.ts,
// a port of the PMS suite.

export function formatUsPhone(value: string): string {
  const digits = value.replace(/\D/g, "").slice(0, 10);
  const len = digits.length;
  if (len === 0) return "";
  if (len < 4) return `(${digits}`;
  if (len < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

// Render a stored E.164 value for display. NOT interchangeable with
// formatUsPhone: that one slices the FIRST 10 digits, which on "+16195550100"
// would keep the country code and drop the last digit.
export function formatE164ForDisplay(
  e164: string,
  extension?: string | null,
): string {
  const national = e164.replace(/\D/g, "").replace(/^1/, "");
  const formatted = formatUsPhone(national);
  return extension ? `${formatted} x${extension}` : formatted;
}

export type NormalizedPhone = {
  ok: true;
  // Canonical +1XXXXXXXXXX — the `phone.e164` dedup key on the PMS side.
  e164: string;
  // Digits only, absent when the input carried no extension.
  extension?: string;
};

export type RejectedPhone = {
  ok: false;
  // Broker-facing and specific — this string is rendered inline on the field,
  // so "US phone numbers only" beats "invalid phone".
  reason: string;
};

// The one rejection the display mask has to recognize (see maskPhoneInput):
// a value naming another country's code must not be reshaped into a US number.
// The string is PMS's verbatim — it is surfaced to the broker as-is.
const NON_US_REASON = "US phone numbers only.";

// Trailing extension in any of the shapes ACORD forms and CRM exports use.
const EXTENSION_RE = /(?:\s*(?:x|ext\.?|#)\s*)(\d+)\s*$/i;

export function normalizeUsPhone(
  input: string,
): NormalizedPhone | RejectedPhone {
  const trimmed = input.trim();

  // Pull the extension off FIRST: otherwise its digits fold into the number
  // and a perfectly good "(619) 555-0100 x12" fails as 12 digits.
  const extMatch = trimmed.match(EXTENSION_RE);
  const extension = extMatch?.[1];
  const withoutExt = extMatch
    ? trimmed.slice(0, trimmed.length - extMatch[0].length)
    : trimmed;

  // An explicit +<cc> where cc isn't 1 is unambiguously not ours. Catching it
  // here gives a better reason than the digit-count check would.
  const plusCountryCode = withoutExt.match(/^\s*\+\s*(\d)/);
  if (plusCountryCode && plusCountryCode[1] !== "1") {
    return { ok: false, reason: NON_US_REASON };
  }

  const digits = withoutExt.replace(/\D/g, "");
  if (digits.length === 0) {
    return { ok: false, reason: "Enter a phone number." };
  }

  let national = digits;
  if (digits.length === 11) {
    if (!digits.startsWith("1")) {
      return { ok: false, reason: NON_US_REASON };
    }
    national = digits.slice(1);
  } else if (digits.length > 11) {
    // Longer than any NANP number — an international number or a mangled value.
    return { ok: false, reason: NON_US_REASON };
  }

  if (national.length !== 10) {
    return { ok: false, reason: "A US phone number must have 10 digits." };
  }

  const area = national.slice(0, 3);
  const exchange = national.slice(3, 6);

  if (area[0] === "0" || area[0] === "1") {
    return { ok: false, reason: "Area code cannot start with 0 or 1." };
  }
  // N11 (911, 411, 611, …) are service codes, never subscriber area codes.
  if (area[1] === "1" && area[2] === "1") {
    return {
      ok: false,
      reason: `${area} is a service code, not a valid area code.`,
    };
  }
  if (exchange[0] === "0" || exchange[0] === "1") {
    return { ok: false, reason: "Exchange code cannot start with 0 or 1." };
  }

  return {
    ok: true,
    e164: `+1${national}`,
    ...(extension ? { extension } : {}),
  };
}

/**
 * What a phone INPUT should display for a value (BRK-S073). The progressive mask
 * alone is not enough, because it corrupts anything carrying a country code:
 * formatUsPhone keeps the FIRST 10 digits, so "+1 (619) 555-0100" becomes
 * "(161) 955-5010" and "+44 20 7946 0958" becomes "(442) 079-4609" — plausible
 * US numbers nobody typed. Three cases instead:
 *
 *   • a value that normalizes → shown canonically (this is what rescues "+1 …");
 *   • anything explicitly country-coded and not ours, or still being typed as
 *     such (a leading "+") → kept VERBATIM, so the broker sees what they pasted
 *     next to "US phone numbers only" rather than a mangled number;
 *   • otherwise → partial US input, masked progressively as they type.
 */
export function maskPhoneInput(value: string): string {
  const result = normalizeUsPhone(value);
  if (result.ok) {
    return formatE164ForDisplay(result.e164, result.extension ?? null);
  }
  if (result.reason === NON_US_REASON || value.trimStart().startsWith("+")) {
    return value;
  }
  return formatUsPhone(value);
}

// ─── Line type (PMS-S173 vocabulary) ─────────────────────────────────────────

// Mirrors the PMS `phoneLineType` pgEnum exactly, in its order (which mirrors
// Lendflow's vocabulary). Line type is intrinsic to the NUMBER, not to who uses
// it, and it is always optional: PMS does no carrier lookup, so "unknown" is a
// legitimate state and the portal never guesses one.
export const PHONE_LINE_TYPES = [
  "mobile",
  "landline",
  "fixed_voip",
  "non_fixed_voip",
  "personal",
  "toll_free",
  "premium",
  "shared_cost",
  "uan",
  "voice_mail",
  "pager",
] as const;

export type PhoneLineType = (typeof PHONE_LINE_TYPES)[number];

const LINE_TYPE_LABELS: Record<PhoneLineType, string> = {
  mobile: "Mobile",
  landline: "Landline",
  fixed_voip: "Fixed VoIP",
  non_fixed_voip: "Non-fixed VoIP",
  personal: "Personal",
  toll_free: "Toll-free",
  premium: "Premium rate",
  shared_cost: "Shared cost",
  uan: "Universal access number",
  voice_mail: "Voice mail",
  pager: "Pager",
};

/** Options for the line-type select, in the contract's order. */
export const PHONE_LINE_TYPE_OPTIONS: {
  value: PhoneLineType;
  label: string;
}[] = PHONE_LINE_TYPES.map((value) => ({
  value,
  label: LINE_TYPE_LABELS[value],
}));

/** Human-readable label for a stored line type (null → no label to show). */
export function lineTypeLabel(lineType: PhoneLineType | null): string | null {
  return lineType ? LINE_TYPE_LABELS[lineType] : null;
}

// ─── The wire shapes (PMS-S173) ──────────────────────────────────────────────

/** A phone as the API RETURNS it: the shared canonical number plus this
 *  owner's link facts (extension, primary). `number` is display-formatted;
 *  `e164` is the canonical value. Arrays come back primary-first. */
export interface PhoneDetail {
  id: string;
  number: string;
  e164: string;
  lineType: PhoneLineType | null;
  extension: string | null;
  isPrimary: boolean;
}

/** A phone as the API ACCEPTS it. `number` is any spelling normalizeUsPhone
 *  accepts (the server re-normalizes); `isPrimary` may be omitted, in which case
 *  the server makes the first entry primary — but this portal always marks one
 *  explicitly so intent is never inferred. */
export interface PhoneInput {
  number: string;
  lineType?: PhoneLineType;
  extension?: string;
  isPrimary?: boolean;
}

// ─── Form values ─────────────────────────────────────────────────────────────

/** One editable phone row. The form's own shape (like AddressValue for
 *  addresses): every field a string so inputs stay controlled, with `lineType`
 *  as "" for unset rather than undefined. */
export interface PhoneValue {
  number: string;
  lineType: PhoneLineType | "";
  /** Carried through edits so a stored extension survives a round-trip; there
   *  is no visible extension input yet (BRK-S073 scope). */
  extension: string;
  isPrimary: boolean;
}

/** A blank first row — primary, because a lone number always is. */
export const EMPTY_PHONE: PhoneValue = {
  number: "",
  lineType: "",
  extension: "",
  isPrimary: true,
};

/** Read shape → editable rows. An owner with no numbers gets one blank row so
 *  the form always has something to type into. */
export function toPhoneValues(
  details: PhoneDetail[] | null | undefined,
): PhoneValue[] {
  if (!details?.length) return [{ ...EMPTY_PHONE }];
  const rows = details.map((d) => ({
    // Mask off the display string: it may carry an " x12" suffix, which lives
    // in `extension` instead. The e164 fallback goes through
    // formatE164ForDisplay, NOT formatUsPhone — the latter would keep the
    // country code and drop the last digit.
    number: d.number ? formatUsPhone(d.number) : formatE164ForDisplay(d.e164),
    lineType: d.lineType ?? ("" as const),
    extension: d.extension ?? "",
    isPrimary: d.isPrimary,
  }));
  return withOnePrimary(rows);
}

/** Editable rows → wire input. Blank rows are DROPPED (a broker who added a row
 *  and left it empty didn't mean to send one), blank line types are omitted
 *  rather than sent as "", and exactly one surviving row is primary. */
export function toPhoneInputs(rows: PhoneValue[]): PhoneInput[] {
  const filled = withOnePrimary(rows.filter((r) => r.number.trim()));
  return filled.map((r) => ({
    number: r.number.trim(),
    ...(r.lineType ? { lineType: r.lineType } : {}),
    ...(r.extension.trim() ? { extension: r.extension.trim() } : {}),
    isPrimary: r.isPrimary,
  }));
}

/** Exactly one primary: the first explicitly-marked row wins, and with none
 *  marked the first row is it. Guards every path that reorders or removes rows —
 *  PMS rejects two primaries outright. */
export function withOnePrimary(rows: PhoneValue[]): PhoneValue[] {
  if (rows.length === 0) return rows;
  const primaryIndex = rows.findIndex((r) => r.isPrimary);
  const chosen = primaryIndex === -1 ? 0 : primaryIndex;
  return rows.map((r, i) => ({ ...r, isPrimary: i === chosen }));
}

/** Per-row rejection reasons, aligned with `rows` (null where the row is fine).
 *  A blank row is not an error — it's dropped on submit. */
export function phoneRowErrors(rows: PhoneValue[]): (string | null)[] {
  return rows.map((r) => {
    if (!r.number.trim()) return null;
    const result = normalizeUsPhone(r.number);
    return result.ok ? null : result.reason;
  });
}

/** Whether a phone list meets the PMS `active`-account floor: at least one
 *  number, one of them marked primary. */
export function hasPrimaryPhone(
  phones: { number?: string; isPrimary?: boolean }[] | null | undefined,
): boolean {
  if (!phones?.length) return false;
  const filled = phones.filter(
    (p) => p.number === undefined || p.number.trim(),
  );
  return filled.length > 0 && filled.some((p) => p.isPrimary);
}
